import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Card, Reveal, PullQuote } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Photo } from '../components/ui/Photo';
import { PHOTOS } from '../data/photos';
import { SERVICE_LINES } from '../data/services';
export function ServicesOverview() {
  return <>
      <section className="surface-dark" data-id="element-3447" data-mp="gv46wc10g93l">
        <Container className="py-16 sm:py-24" data-id="element-3448" data-mp="lu7274r6v0yd">
          <Kicker data-id="element-3449" data-mp="lsvyf4xqtt79">Services</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-3450" data-mp="qtjyeg3y9kdm">
            Three lines, one method, in the order the work usually needs
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-3451" data-mp="vqacixw725we">
            Intelligence establishes where the value is. Growth builds the route to it. AI makes the whole thing
            repeatable without adding headcount. You can start anywhere. Most clients start with Intelligence.
          </p>
        </Container>
      </section>

      <Section data-id="element-3452" data-mp="68s9ur8yb18g">
        <div className="grid gap-6 lg:grid-cols-3" data-id="element-3453" data-mp="68a8jg7flwto">
          {SERVICE_LINES.map((line, index) => <Reveal key={line.id} delay={index * 0.06} data-id="element-3454" data-mp="1aqrk8jcn5y1" data-mpi={index}>
              <Card className="flex h-full flex-col" data-id="element-3455" data-mp="3sgjwd81p5shg">
                <span className="font-display text-[13px] text-gold" data-id="element-3456" data-mp="3si3df0v6g7ka">Step 0{index + 1}</span>
                <h2 className="mt-3 font-display text-[26px] leading-tight text-ink" data-id="element-3457" data-mp="ty4kzwnu9jde">{line.name}</h2>
                <p className="mt-3 text-[15px] text-body" data-id="element-3458" data-mp="p0349idvxw53">{line.promise}</p>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-3459" data-mp="jzfe0yxhdjbx">Who it is for</p>
                <p className="mt-2 text-[14px] text-body" data-id="element-3460" data-mp="fybv23k4z76h">{line.who}</p>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-3461" data-mp="34qxuudxur16a">The outcome</p>
                <p className="mt-2 text-[14px] text-body" data-id="element-3462" data-mp="30ob2mbx9re8k">{line.outcome}</p>
                <Link to={`/services/${line.id}`} className="mt-auto inline-flex items-center gap-2 pt-7 text-[15px] font-medium text-royal underline-offset-4 hover:underline" data-id="element-3463" data-mp="325zx90z7bn23">
                  Explore this line
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3464" data-mp="2x76iz6c3bs0v" />
                </Link>
              </Card>
            </Reveal>)}
        </div>
      </Section>

      <Section tone="champagne" aria-labelledby="cavalla-gov" data-id="element-3465" data-mp="2jegsoppj40xy">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center" data-id="element-3466" data-mp="2o7jqxb593ykh">
          <div data-id="element-3467" data-mp="29i4240pa637f">
            <Kicker data-id="element-3468" data-mp="29in9inhb1977">Our platform for government</Kicker>
            <h2 id="cavalla-gov" className="mt-4 font-display text-[28px] leading-tight text-ink sm:text-[36px]" data-id="element-3469" data-mp="2egotrbqgvek9">
              Cavalla Gov
            </h2>
            <p className="mt-5 max-w-xl text-[17px] text-body" data-id="element-3470" data-mp="20hfp99bmi8w4">
              A platform in its own right, distinct from the three advisory lines but drawing on all of them. It
              gives a ministry of finance one live picture of how the national development plan is being
              delivered, and where development partner funding lands against it.
            </p>
            <Button to="/cavalla-gov" size="lg" shape="pill" className="mt-8" data-id="element-3471" data-mp="1uvyd4uicnc51">
              See the platform
            </Button>
          </div>
          <Photo photo={PHOTOS.workshopRoom} aspect="aspect-[16/10]" data-id="element-3472" data-mp="1pxww0f4hhmw7" />
        </div>
      </Section>

      <Section tone="mist" data-id="element-3473" data-mp="24qyr4079jmfr">
        <div className="grid gap-12 lg:grid-cols-2" data-id="element-3474" data-mp="1zu8c7g772wvx">
          <SectionHeader kicker="How they connect" title="The path to depth" lead="Each line stands alone. Together they compound, because the evidence gathered in one becomes the input to the next." data-id="element-3475" data-mp="1ztq49mb66u86" />
          <div className="space-y-6" data-id="element-3476" data-mp="2nj185cnth4qj">
            <PullQuote data-id="element-3477" data-mp="2nk2jzjcnpv9b">
              A market study that never becomes a plan is an expensive opinion. A plan without evidence is a
              guess with a deadline.
            </PullQuote>
            <p className="text-[15px] text-body" data-id="element-3478" data-mp="32lsxugrlcwck">
              We built the three lines so a client can begin with the question they actually have, rather than the
              engagement a firm wants to sell. If you know your market and need demand, start with Growth. If you
              are being pressed on AI and cannot tell hype from opportunity, start with AI.
            </p>
            <Button to="/augmented-intelligence" variant="secondary" data-id="element-3479" data-mp="2jorpclytwe3v">
              Read our method
            </Button>
          </div>
        </div>
      </Section>

      <section className="surface-dark" data-id="element-3480" data-mp="2soo866yjeir0">
        <Container className="py-16" data-id="element-3481" data-mp="2snmya5t9c2av">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center" data-id="element-3482" data-mp="2sqr75gho3m7v">
            <h2 className="max-w-xl font-display text-[28px] leading-tight text-white" data-id="element-3483" data-mp="7y5wplsl9ntw">
              Not certain which line fits your question
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row" data-id="element-3484" data-mp="cvp90g5q7qj7">
              <Button to="/contact" size="lg" data-id="element-3485" data-mp="1xfudvpabhcq1">Book a discovery call</Button>
              <Button to="/assessment" variant="reversed" size="lg" data-id="element-3486" data-mp="1sg811gyehyhr">Take the assessment</Button>
            </div>
          </div>
        </Container>
      </section>
    </>;
}